<template>
  <div class="inline-multiselect" :style="{ maxHeight: height }">
    <div
      v-if="showSelectedItems && selectedItems.length > 0"
      class="selected-items-area"
    >
      <div class="selected-items-header">
        <span class="selected-count">{{ selectedItems.length }} selected</span>
      </div>
      <div class="selected-items-list">
        <div
          v-for="item in displayedSelectedItems"
          :key="'selected-' + item[valueKey]"
          class="selected-item"
          @click="toggleSelect(item)"
        >
          <span class="selected-item-label">
            <slot name="selectedLabel" :item="item">
              {{ item[labelKey] }}
            </slot>
          </span>
          <span class="selected-item-remove">×</span>
        </div>
        <div
          v-if="remainingSelectedCount > 0"
          class="selected-item remaining-count"
        >
          +{{ remainingSelectedCount }} more
        </div>
      </div>
    </div>
    <div class="inline-search">
      <input
        v-model.trim="search"
        class="form-control search-input"
        :placeholder="placeholder"
      />
    </div>
    <div
      class="inline-list"
      :style="{
        maxHeight:
          showSelectedItems && selectedItems.length > 0
            ? `calc(${height} - 144px)`
            : `calc(${height} - 78px)`,
      }"
    >
      <div
        v-if="filteredOptions.length > 0"
        class="inline-list-item select-all-item"
        @click.stop="toggleSelectAll"
      >
        <div class="custom-control custom-checkbox">
          <input
            type="checkbox"
            class="custom-control-input"
            id="select-all-checkbox"
            :checked="allFilteredSelected"
            :indeterminate.prop="someFilteredSelected && !allFilteredSelected"
          />
          <span
            class="custom-control-label"
            for="select-all-checkbox"
            style="font-weight: 500"
          >
            Select All
          </span>
        </div>
      </div>
      <div
        v-for="option in filteredOptions"
        :key="option[valueKey]"
        class="inline-list-item"
        :class="{
          selected: isSelected(option),
        }"
        @click="toggleSelect(option)"
      >
        <div class="custom-control custom-checkbox my-2">
          <input
            type="checkbox"
            class="custom-control-input"
            :checked="isSelected(option)"
          />
          <div class="custom-control-label">
            <slot name="label" :item="option">
              {{ option[labelKey] }}
            </slot>
          </div>
        </div>
      </div>
      <div
        v-if="filteredOptions.length === 0"
        class="text-center text-muted py-3"
      >
        {{ noResultMessage }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InlineMultiSelect",
  model: {
    prop: "value",
    event: "change",
  },
  props: {
    options: { type: Array, required: true },
    value: { type: Array, default: () => [] },
    labelKey: { type: String, default: "label" },
    valueKey: { type: String, default: "value" },
    placeholder: { type: String, default: "Select..." },
    height: { type: String, default: "380px" },
    noResultMessage: { type: String, default: "No results found" },
    keepSelectedAtTop: { type: Boolean, default: true },
    showSelectedItems: { type: Boolean, default: true },
    maxSelectedDisplay: { type: Number, default: 3 },
  },
  data() {
    return {
      search: "",
      sortedOptions: [],
    };
  },
  computed: {
    selectedItems() {
      return this.options.filter((opt) =>
        this.value.includes(opt[this.valueKey])
      );
    },
    displayedSelectedItems() {
      return this.selectedItems.slice(0, this.maxSelectedDisplay);
    },
    remainingSelectedCount() {
      return Math.max(0, this.selectedItems.length - this.maxSelectedDisplay);
    },
    filteredOptions() {
      const term = this.search.toLowerCase();
      const optionsToFilter =
        this.sortedOptions.length > 0 ? this.sortedOptions : this.options;
      return optionsToFilter.filter((opt) =>
        (opt[this.labelKey] + "").toLowerCase().includes(term)
      );
    },
    allFilteredSelected() {
      if (this.filteredOptions.length === 0) return false;
      return this.filteredOptions.every((opt) =>
        this.value.includes(opt[this.valueKey])
      );
    },
    someFilteredSelected() {
      return (
        this.filteredOptions.some((opt) =>
          this.value.includes(opt[this.valueKey])
        ) && !this.allFilteredSelected
      );
    },
  },
  mounted() {
    this.initializeSorting();
  },
  watch: {
    options: {
      handler() {
        this.initializeSorting();
      },
      immediate: true,
    },
  },
  methods: {
    initializeSorting() {
      if (this.keepSelectedAtTop && this.value.length > 0) {
        this.sortedOptions = [...this.options].sort((a, b) => {
          const aSelected = this.value.includes(a[this.valueKey]);
          const bSelected = this.value.includes(b[this.valueKey]);

          if (aSelected && !bSelected) return -1;
          if (!aSelected && bSelected) return 1;
          return 0;
        });
      } else {
        this.sortedOptions = [...this.options];
      }
    },
    isSelected(option) {
      return this.value.includes(option[this.valueKey]);
    },
    toggleSelect(option) {
      const val = option[this.valueKey];
      const newValue = this.value.includes(val)
        ? this.value.filter((v) => v !== val)
        : [...this.value, val];
      this.$emit("change", newValue);
    },
    toggleSelectAll() {
      const filteredValues = this.filteredOptions.map(
        (opt) => opt[this.valueKey]
      );
      if (this.allFilteredSelected) {
        const newValue = this.value.filter((v) => !filteredValues.includes(v));
        this.$emit("change", newValue);
      } else {
        const newSet = new Set([...this.value, ...filteredValues]);
        this.$emit("change", Array.from(newSet));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.inline-multiselect {
  position: relative;
  min-width: 250px;
  font-family: inherit;
  background: #fff;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: fit-content;
  .inline-search {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 2;
    padding: 8px 8px 4px 8px;
    border-bottom: 1px solid #e0e0e0;
    .search-input {
      border: 1px solid #bdbdbd;
      font-size: 14px;
      padding: 6px 8px;
    }
  }
  .inline-list {
    overflow-y: auto;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    .inline-list-item {
      width: 100%;
      display: flex;
      align-items: center;
      padding: 0 12px;
      cursor: pointer;
      transition: background 0.2s;
      border: none;
      outline: none;
      background: #fff;

      &.selected {
        background-color: var(--theme-color);
      }
      &:hover:not(.selected) {
        filter: brightness(0.95);
      }

      &.select-all-item {
        position: sticky;
        top: 0;
        z-index: 10;
        background: #f8f9fa !important;
        border-bottom: 1px solid #e0e0e0 !important;
        min-height: 40px;

        &:hover {
          background: #f8f9fa !important;
          filter: brightness(0.95);
        }
      }
    }
  }

  .selected-items-area {
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    padding: 8px 12px;
    max-height: 60px;
    overflow: hidden;

    .selected-items-header {
      font-size: 12px;
      color: #6c757d;
      margin-bottom: 4px;
      font-weight: 500;
    }

    .selected-items-list {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      max-height: 32px;
      overflow: hidden;

      .selected-item {
        display: inline-flex;
        align-items: center;
        background: var(--theme-color, #007bff);
        color: black;
        padding: 2px 6px;
        border-radius: 12px;
        font-size: 12px;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
          filter: brightness(0.95);
        }

        &.remaining-count {
          background: #000000;
          color: white;
          cursor: default;

          &:hover {
            background: #000000;
          }
        }

        .selected-item-label {
          margin-right: 4px;
          max-width: 100px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .selected-item-remove {
          font-weight: bold;
          opacity: 0.8;

          &:hover {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
